import { v4 as uuidv4 } from 'uuid';

export const TARA_SESSION_KEY_PREFIX = 'tara_session_id_';

export const getOrCreateAgentSessionId = (agentKey: string): string => {
  const key = `${TARA_SESSION_KEY_PREFIX}${agentKey}`;
  try {
    const existing = localStorage.getItem(key);
    if (existing) return existing;
  } catch (e) {
    console.warn('Failed to read Tara session ID from localStorage', e);
  }
  const newId = uuidv4();
  try {
    localStorage.setItem(key, newId);
  } catch (e) {
    console.warn('Failed to save Tara session ID to localStorage', e);
  }
  return newId;
};

export const initializeAgentSessionIds = () => {
  getOrCreateAgentSessionId('hr');
  getOrCreateAgentSessionId('recruitment');
};

export const clearTaraSessionId = () => {
  try {
    localStorage.removeItem('tara_session_id');
  } catch (e) {
    console.warn('Failed to clear Tara session ID from localStorage', e);
  }
};

export const clearAllAgentSessionIds = () => {
  try {
    Object.keys(localStorage)
      .filter((k) => k.startsWith(TARA_SESSION_KEY_PREFIX))
      .forEach((k) => localStorage.removeItem(k));
  } catch (e) {
    console.warn('Failed to clear agent session IDs from localStorage', e);
  }
};

export const TARA_DOCUMENTS_KEY_PREFIX = 'tara_documents_';

export const getAgentSessionDocumentsKey = (agentKey: string) =>
  `${TARA_DOCUMENTS_KEY_PREFIX}${agentKey}`;

export const saveAgentSessionDocument = (
  agentKey: string,
  document: { documentId: string; fileName: string; fileType: string } | null
) => {
  const key = getAgentSessionDocumentsKey(agentKey);
  try {
    if (document) {
      localStorage.setItem(key, JSON.stringify(document));
    } else {
      localStorage.removeItem(key);
    }
  } catch (e) {
    console.warn('Failed to save agent session document to localStorage', e);
  }
};

export const loadAgentSessionDocument = (
  agentKey: string
): { documentId: string; fileName: string; fileType: string } | null => {
  const key = getAgentSessionDocumentsKey(agentKey);
  try {
    const saved = localStorage.getItem(key);
    if (saved) return JSON.parse(saved);
  } catch (e) {
    console.warn('Failed to load agent session document from localStorage', e);
  }
  return null;
};

export const clearAgentSessionDocument = (agentKey: string) => {
  saveAgentSessionDocument(agentKey, null);
};

// Tracks all document IDs uploaded within the current agent session.
// Used to send the full session document context with each query payload.
export const TARA_SESSION_DOCUMENT_IDS_KEY_PREFIX = 'tara_session_document_ids_';

export const getAgentSessionDocumentIdsKey = (agentKey: string) =>
  `${TARA_SESSION_DOCUMENT_IDS_KEY_PREFIX}${agentKey}`;

export const addAgentSessionDocumentId = (
  agentKey: string,
  documentId: string
) => {
  const key = getAgentSessionDocumentIdsKey(agentKey);
  try {
    const existing = localStorage.getItem(key);
    let ids: string[] = existing ? JSON.parse(existing) : [];
    if (!Array.isArray(ids)) ids = [];
    if (!ids.includes(documentId)) {
      ids.push(documentId);
      localStorage.setItem(key, JSON.stringify(ids));
    }
  } catch (e) {
    console.warn('Failed to add agent session document ID:', e);
  }
};

export const getAgentSessionDocumentIds = (
  agentKey: string
): string[] => {
  const key = getAgentSessionDocumentIdsKey(agentKey);
  try {
    const saved = localStorage.getItem(key);
    if (saved) {
      const parsed = JSON.parse(saved);
      if (Array.isArray(parsed)) return parsed;
    }
  } catch (e) {
    console.warn('Failed to load agent session document IDs:', e);
  }
  return [];
};

export const removeAgentSessionDocumentId = (
  agentKey: string,
  documentId: string
) => {
  const key = getAgentSessionDocumentIdsKey(agentKey);
  try {
    const existing = localStorage.getItem(key);
    if (existing) {
      const ids: string[] = JSON.parse(existing);
      const filtered = ids.filter((id) => id !== documentId);
      if (filtered.length > 0) {
        localStorage.setItem(key, JSON.stringify(filtered));
      } else {
        localStorage.removeItem(key);
      }
    }
  } catch (e) {
    console.warn('Failed to remove agent session document ID:', e);
  }
};

export const clearAgentSessionDocumentIds = (agentKey: string) => {
  const key = getAgentSessionDocumentIdsKey(agentKey);
  try {
    localStorage.removeItem(key);
  } catch (e) {
    console.warn('Failed to clear agent session document IDs:', e);
  }
};

export const clearAllAgentSessionDocumentIds = () => {
  try {
    Object.keys(localStorage)
      .filter((k) => k.startsWith(TARA_SESSION_DOCUMENT_IDS_KEY_PREFIX))
      .forEach((k) => localStorage.removeItem(k));
  } catch (e) {
    console.warn('Failed to clear all agent session document IDs:', e);
  }
};

export const PRESERVE_LOCAL_STORAGE_KEYS = new Set([
  'termsAccepted',
  'access_code',
]);

export const clearAllAppStorage = () => {
  try {
    Object.keys(localStorage).forEach((key) => {
      if (!PRESERVE_LOCAL_STORAGE_KEYS.has(key)) {
        localStorage.removeItem(key);
      }
    });
    sessionStorage.clear();
  } catch (e) {
    console.warn('Failed to clear app storage:', e);
  }
};
